import { Link } from "react-router-dom";
import type { Post } from "@/mocks/posts";
import { categories } from "@/mocks/categories";
import { formatViews } from "@/components/feature/PostCard";

interface HeroSectionProps {
  featured: Post;
  sidePosts: Post[];
  todayCount: number;
  dateLabel: string;
}

export default function HeroSection({
  featured,
  sidePosts,
  todayCount,
  dateLabel,
}: HeroSectionProps) {
  const featuredCategory = categories.find((c) => c.id === featured.categoryId);

  return (
    <section className="border-b border-background-200 bg-background-100">
      <div className="w-full px-4 py-10 md:px-6 md:py-14">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4 border-b border-background-300 pb-5">
          <div>
            <p className="flex items-center gap-2 text-sm font-medium text-primary-700">
              <i className="ri-calendar-check-line"></i>
              {dateLabel}
            </p>
            <h1 className="mt-2 font-heading text-3xl font-black leading-tight tracking-tight text-foreground-950 md:text-5xl">
              今日更新帖 · 每日热点一次看完
            </h1>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <div>
              <p className="font-heading text-2xl font-black text-foreground-950">{todayCount}</p>
              <p className="text-foreground-500">今日新增</p>
            </div>
            <div className="h-10 w-px bg-background-300"></div>
            <div>
              <p className="font-heading text-2xl font-black text-foreground-950">6</p>
              <p className="text-foreground-500">内容方向</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <article className="lg:col-span-7">
            <Link to={`/post/${featured.id}`} className="group block cursor-pointer">
              <div className="relative h-64 w-full overflow-hidden rounded-xl bg-background-200 md:h-96">
                <img
                  src={featured.cover}
                  alt={`${featured.title}焦点配图`}
                  className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/25 to-transparent"></div>
                <span className="absolute left-4 top-4 flex items-center gap-1 rounded-md bg-accent-500 px-2.5 py-1 text-xs font-bold text-foreground-950">
                  <i className="ri-focus-3-line"></i>
                  今日焦点
                </span>
                <div className="absolute inset-x-0 bottom-0 p-4 md:p-6">
                  <span className="rounded-md bg-primary-600 px-2 py-0.5 text-xs font-medium text-primary-50">
                    {featuredCategory?.name ?? "资讯"}
                  </span>
                  <h2 className="mt-3 font-heading text-xl font-black leading-snug text-background-50 md:text-3xl">
                    {featured.title}
                  </h2>
                  <p className="mt-2 line-clamp-2 text-sm text-background-200">
                    {featured.summary}
                  </p>
                </div>
              </div>
            </Link>
          </article>

          <div className="lg:col-span-5">
            <div className="flex h-full flex-col">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  今日更新帖
                </h2>
                <Link
                  to="/archive"
                  className="cursor-pointer whitespace-nowrap text-sm font-medium text-primary-700 transition-colors hover:text-primary-800"
                >
                  查看归档 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="flex flex-1 flex-col gap-3">
                {sidePosts.map((post, index) => (
                  <Link
                    key={post.id}
                    to={`/post/${post.id}`}
                    className="group flex cursor-pointer items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-3.5 transition-colors hover:border-primary-300"
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-secondary-100 font-heading text-sm font-black text-secondary-800">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-foreground-900 transition-colors group-hover:text-primary-700">
                        {post.title}
                      </h3>
                      <div className="mt-1.5 flex items-center gap-3 text-xs text-foreground-500">
                        <span>{post.date}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {formatViews(post.views)}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}